import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import articles from '../../data/articlesData';

const Articles = () => {
  useEffect(() => {
    document.title = 'Articles — Dmitri Malisev';
  }, []);

  return (
    <Container>
      <PageHeader>
        <SectionLabel>Articles</SectionLabel>
        <PageTitle>Technical writeups</PageTitle>
        <PageSubtitle>Guides, deep-dives, and things worth writing down.</PageSubtitle>
      </PageHeader>

      {articles.length === 0 ? (
        <ComingSoon>
          <ComingSoonMono>{'// coming soon'}</ComingSoonMono>
          <ComingSoonText>Articles are being written. Check back later.</ComingSoonText>
        </ComingSoon>
      ) : (
        <ArticleList>
          {articles.map(article => (
            <ArticleCard key={article.slug} to={`/articles/${article.slug}`}>
              <ArticleMeta>
                <ArticleDate>{article.date}</ArticleDate>
                <ArticleTags>
                  {article.tags.map(tag => <ArticleTag key={tag}>{tag}</ArticleTag>)}
                </ArticleTags>
              </ArticleMeta>
              <ArticleTitle>{article.title}</ArticleTitle>
              <ArticleDesc>{article.description}</ArticleDesc>
              <ReadMore>Read →</ReadMore>
            </ArticleCard>
          ))}
        </ArticleList>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 4rem 0;
`;

const PageHeader = styled.div`
  margin-bottom: 3rem;
`;

const SectionLabel = styled.p`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--accent);
  margin: 0 0 0.5rem;
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.5rem;
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    font-size: 1.375rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1rem;
  color: var(--text-primary);
  margin: 0;
`;

const ComingSoon = styled.div`
  padding: 3rem 0;
`;

const ComingSoonMono = styled.p`
  font-family: var(--font-mono);
  font-size: 0.9375rem;
  color: var(--accent);
  margin: 0 0 0.75rem;
`;

const ComingSoonText = styled.p`
  font-size: 1rem;
  color: var(--text-muted);
  margin: 0;
`;

const ArticleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  border: 1px solid var(--border);
  border-radius: 6px;
  overflow: hidden;
`;

const ArticleCard = styled(Link)`
  display: block;
  padding: 1.5rem;
  background: var(--bg-surface);
  text-decoration: none;
  border-bottom: 1px solid var(--border);
  transition: background 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--bg-surface-2);
  }
`;

const ArticleMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const ArticleDate = styled.span`
  font-family: var(--font-mono);
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const ArticleTags = styled.div`
  display: flex;
  gap: 0.375rem;
`;

const ArticleTag = styled.span`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  padding: 0.15rem 0.45rem;
  border: 1px solid var(--border);
  border-radius: 3px;
  color: var(--text-muted);
`;

const ArticleTitle = styled.h2`
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.375rem;
`;

const ArticleDesc = styled.p`
  font-size: 0.875rem;
  color: var(--text-muted);
  margin: 0 0 0.75rem;
  line-height: 1.6;
`;

const ReadMore = styled.span`
  font-size: 0.8125rem;
  color: var(--accent);
`;

export default Articles;
